import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { HomeLayoutShell } from '../components/home/layout/HomeLayoutShell';

interface SiteContent {
  slug: string;
  title: string;
  content: string;
  updatedAt: string | null;
}

/**
 * 站点内容页（运维平台「内容管理」消费端）：页脚/登录弹窗协议链接经
 * OPEN_CONTENT_EVENT → AppNew content 视图进入，slug 由 query 携带（刷新可恢复）。
 * 壳与 ToolsPage 同款（#121212 + HomeLayoutShell 828 列）；正文纯文本 pre-line 渲染，
 * 404/失败/未带 slug 显示空态。数据公开接口，无需登录。
 */
export const ContentPage: React.FC<{ slug: string | null }> = ({ slug }) => {
  const [data, setData] = useState<SiteContent | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      return;
    }
    let disposed = false;
    setData(null);
    setNotFound(false);
    fetch(`/api/site-contents/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((row) => {
        if (!disposed) setData(row);
      })
      .catch(() => {
        if (!disposed) setNotFound(true);
      });
    return () => {
      disposed = true;
    };
  }, [slug]);

  return (
    <div className="w-full min-h-screen pt-24 pb-16" style={{ backgroundColor: '#121212' }}>
      <HomeLayoutShell>
        <div className="relative w-full max-w-[828px] mx-auto">
          {data ? (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <FileText size={20} className="text-zinc-200" />
                </div>
                <h1 className="text-2xl font-semibold text-white">{data.title}</h1>
              </div>
              <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-8 py-6">
                <div className="whitespace-pre-line text-[15px] leading-7 text-zinc-300">
                  {data.content}
                </div>
                {data.updatedAt && (
                  <div className="mt-8 text-xs text-zinc-600">
                    最近更新：{new Date(data.updatedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 py-24 text-center">
              {notFound ? (
                <>
                  <p className="text-zinc-400">内容不存在或已下线</p>
                  <p className="text-sm text-zinc-600 mt-2">请稍后再试，或联系客服咨询</p>
                </>
              ) : (
                <p className="text-zinc-500">加载中...</p>
              )}
            </div>
          )}
        </div>
      </HomeLayoutShell>
    </div>
  );
};
